import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostBinding,
  input,
  viewChild,
} from '@angular/core';

import { RouterModule } from '@angular/router';
import {
  IPageLayoutSettings,
  PAGE_LAYOUT,
  PageLayoutSettings,
  TPageLayout,
  PropExtend,
  UTILS,
  TPageLayoutAreaSizes,
  TPageLayoutRouteOutlets,
} from '@aksia/infrastructure';
import { UiControlDirective } from '../../../directives/ui/ui-control.directive';

const DEFAULT_ROUTE_OUTLETS: TPageLayoutRouteOutlets = {
  TOP: 'top',
  BOTTOM: 'bottom',
  CENTER: undefined,
  LEFT: 'left',
  RIGHT: 'right',
};

@Component({
  selector: 'page-layout',
  imports: [RouterModule],
  templateUrl: './page-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageLayoutComponent extends UiControlDirective {
  //#region Inputs

  override settings = input<PageLayoutSettings, IPageLayoutSettings>(
    new PageLayoutSettings(),
    {
      transform: (settings: IPageLayoutSettings) => this.initSettings(settings),
    },
  );

  //#endregion

  //#region View Children

  topSlot = viewChild<ElementRef<HTMLDivElement>>('topSlot');
  topHandle = viewChild<ElementRef<HTMLDivElement>>('topHandle');
  bottomSlot = viewChild<ElementRef<HTMLDivElement>>('bottomSlot');
  bottomHandle = viewChild<ElementRef<HTMLDivElement>>('bottomHandle');
  leftSlot = viewChild<ElementRef<HTMLDivElement>>('leftSlot');
  leftHandle = viewChild<ElementRef<HTMLDivElement>>('leftHandle');
  rightSlot = viewChild<ElementRef<HTMLDivElement>>('rightSlot');
  rightHandle = viewChild<ElementRef<HTMLDivElement>>('rightHandle');

  //#endregion

  //#region Host Bindings

  @HostBinding('attr.uipagelayout')
  get layoutTypeAttribute() {
    return this.settings().type;
  }

  @HostBinding('style.grid-template-rows')
  get gridTemplateRows() {
    let rows = `${this.settings()?.type?.includes(PAGE_LAYOUT.TOP) ? ' var(--top-area-height) ' : ' '}${this.settings()?.handles?.includes(PAGE_LAYOUT.TOP) ? ' var(--page-layout-handle-height) ' : ' '}var(--center-area-height)${this.settings()?.handles?.includes(PAGE_LAYOUT.BOTTOM) ? ' var(--page-layout-handle-height) ' : ' '}${this.settings()?.type?.includes(PAGE_LAYOUT.BOTTOM) ? ' var(--bottom-area-height) ' : ' '}`;
    return rows;
  }

  @HostBinding('style.grid-template-columns')
  get gridTemplateColumns() {
    let cols = `${this.settings()?.type?.includes(PAGE_LAYOUT.LEFT) ? ' var(--left-area-width) ' : ' '}${this.settings()?.handles?.includes(PAGE_LAYOUT.LEFT) ? ' var(--handle-width) ' : ' '}var(--center-area-width)${this.settings()?.handles?.includes(PAGE_LAYOUT.RIGHT) ? ' var(--handle-width) ' : ' '}${this.settings()?.type?.includes(PAGE_LAYOUT.RIGHT) ? ' var(--right-area-width) ' : ' '}`;
    return cols;
  }

  //#endregion

  //#region Properties

  protected readonly LAYOUT: PropExtend<TPageLayout> = PAGE_LAYOUT;
  protected readonly ROUTE_OUTLETS: TPageLayoutRouteOutlets =
    DEFAULT_ROUTE_OUTLETS;
  protected cssVariableResize!: string;
  protected get areaSizesField() {
    return this.cssVariableResize?.replace(
      /-/g,
      '',
    ) as keyof TPageLayoutAreaSizes;
  }
  private resizeObserver: ResizeObserver | undefined = undefined;

  //#endregion

  //#region Functions

  constructor() {
    super();
    effect(() => {
      if (this.settings()) {
        this.setDefaultAreaSizes();
      }
    });

    afterNextRender(() => {
      //this.updateCenterAreaSize();
      /* this.resizeObserver = new ResizeObserver((entries) => {
        this.updateCenterAreaSize();
      }); */
      /* this.resizeObserver.observe(this.hostElRef.nativeElement); */
      /* this.destroyRef.onDestroy(() => {
        this.resizeObserver?.disconnect();
      }); */
    });
  }

  protected override initSettings(
    settings: IPageLayoutSettings | undefined,
  ): PageLayoutSettings {
    let pageLayoutSettings = UTILS.OBJECT.assign(
      new PageLayoutSettings(),
      settings,
    ) as PageLayoutSettings;
    return pageLayoutSettings;
  }

  protected startDrag(e: DragEvent, cssVariable: string) {
    this.cssVariableResize = cssVariable;
    /* const proxyEl = (e.target as HTMLElement)?.cloneNode(true) as HTMLElement;
      e.dataTransfer!.setDragImage(proxyEl, 0, 0); */
  }

  protected drag(e: DragEvent) {
    e.preventDefault();
  }

  protected endDrag(e: DragEvent) {
    let position = this.calculatePosition(e);
    this.hostElRef.nativeElement.style.setProperty(
      this.cssVariableResize,
      position,
    );
    //this.updateCenterAreaSize();
    this.cssVariableResize = '';
    e.preventDefault();
  }

  protected toggle(cssVariable: string) {
    this.cssVariableResize = cssVariable;
    let layoutDefaultSizesValue =
      this.settings().areaSizes?.[this.areaSizesField] ?? 'fit-content(100%)';
    this.hostElRef.nativeElement.style.setProperty(
      cssVariable,
      layoutDefaultSizesValue,
    );
    //this.updateCenterAreaSize();
    this.cssVariableResize = '';
  }

  private calculatePosition(e: DragEvent) {
    const position: TPageLayoutAreaSizes = {
      topareaheight: `${e.clientY}px`,
      bottomareaheight: `${document.body.clientHeight - e.clientY}px`,
      leftareawidth: `${e.clientX}px`,
      rightareawidth: `${this.hostElRef.nativeElement.clientWidth - e.clientX}px`,
    };
    return position[this.areaSizesField];
  }

  private setDefaultAreaSizes() {
    let layoutMaxHeight =
      (this.hostElRef.nativeElement as HTMLElement)?.offsetParent?.tagName ===
      'BODY'
        ? '100vh'
        : '100%';

    let layoutMaxWidth =
      (this.hostElRef.nativeElement as HTMLElement)?.offsetParent?.tagName ===
      'BODY'
        ? '100vw'
        : '100%';

    this.hostElRef.nativeElement.style.setProperty(
      '--layout-height',
      layoutMaxHeight,
    );
    this.hostElRef.nativeElement.style.setProperty(
      '--layout-width',
      layoutMaxWidth,
    );

    [
      '--top-area-height',
      '--bottom-area-height',
      '--left-area-width',
      '--right-area-width',
    ].forEach((cssVariable) => {
      let areaSizeField = cssVariable.replace(
        /-/g,
        '',
      ) as keyof TPageLayoutAreaSizes;
      this.hostElRef.nativeElement.style.setProperty(
        cssVariable,
        this.settings().areaSizes?.[areaSizeField] ?? '0px',
      );
    });

    this.hostElRef.nativeElement.style.setProperty(
      '--center-area-height',
      'calc(var(--layout-height) - var(--top-area-height) - var(--bottom-area-height))',
    );

    this.hostElRef.nativeElement.style.setProperty(
      '--center-area-width',
      'calc(var(--layout-width) - var(--left-area-width) - var(--right-area-width))',
    );

    //this.updateCenterAreaSize();
  }

  /* private updateCenterAreaSize(areaSlot?: string, areaSize?: number) {
    let scrollbarWidth = this.settings()?.includeScrollbarArea
      ? UTILS.UI.remToPixels(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--spacing-scrollbar-width',
          ),
        )
      : 0;
    let parentHeight =
      this.hostElRef.nativeElement.parentElement.clientHeight + scrollbarWidth;

    let topHeight =
      areaSlot === '--top-area-height'
        ? areaSize!
        : (this.topSlot()?.nativeElement.clientHeight ?? 0);
    let topHandleHeight = this.topHandle()?.nativeElement.clientHeight ?? 0;
    let bottomHeight =
      areaSlot === '--bottom-area-height'
        ? areaSize!
        : (this.bottomSlot()?.nativeElement.clientHeight ?? 0);
    let bottomHandleHeight =
      this.bottomHandle()?.nativeElement.clientHeight ?? 0;
    let leftWidth =
      areaSlot === '--left-area-width'
        ? areaSize!
        : (this.leftSlot()?.nativeElement.clientWidth ?? 0);
    let leftHandleWidth = this.leftHandle()?.nativeElement.clientWidth ?? 0;
    let rightWidth =
      areaSlot === '--right-area-width'
        ? areaSize!
        : (this.rightSlot()?.nativeElement.clientWidth ?? 0);
    let rightHandleWidth = this.rightHandle()?.nativeElement.clientWidth ?? 0;

    if (
      this.settings()?.type === PAGE_LAYOUT.CENTER ||
      this.settings()?.type === PAGE_LAYOUT.BLANK
    ) {
      this.hostElRef.nativeElement.style.setProperty(
        '--center-area-height',
        'auto',
      );
    } else {
      this.hostElRef.nativeElement.style.setProperty(
        '--center-area-height',
        `${parentHeight - topHeight - topHandleHeight - bottomHeight - bottomHandleHeight}px`,
      );
    }

    this.hostElRef.nativeElement.style.setProperty(
      '--center-area-width',
      `${this.hostElRef.nativeElement.clientWidth - scrollbarWidth - leftWidth - leftHandleWidth - rightWidth - rightHandleWidth}px`,
    );
  } */

  //#endregion
}
